import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnInit,
} from '@angular/core';
import { AbstractControl, ValidationErrors } from '@angular/forms';

import { TranslateModule } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { delay, tap } from 'rxjs/operators';

import { ErrorTexts } from '@personal-area/global/form-validation/models';

enum FieldType {
  text = 'text',
  checkbox = 'checkbox',
  date = 'date',
  hidden = 'hidden',
  custom = 'custom'
}

@Component({
  selector: 'app-form-field',
  templateUrl: './form-field.component.html',
  styleUrls: ['./form-field.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
  ]
})
export class FormFieldComponent implements OnInit {

  public types = FieldType;

  public clientValidationAnimation = false;

  @Input() ctx!: { field: string; control: AbstractControl };

  @Input() newFormField = false;

  @Input() labelIsVisible = false;

  @Input() class: string;
  @Input() type: FieldType | string = FieldType.text;
  @Input() errorTexts: ErrorTexts = {};
  @Input() errorParams: { [param: string]: string } = {};
  @Input() submitErrors!: Observable<void>;
  @Input() reset!: Observable<void>;
  @Input() isControlTouched = false;
  @Input() isControlInFocus = false;
  @Input() errors: ValidationErrors = {};
  @Input() errorsShownOnBlur: string[];
  @Input() errorsShownOnSubmit: string[];
  @Input() errorsShownOnControlTouched: string[];

  private _isFormSubmitted = false;

  private _errorsToShow: string[] = [];

  constructor(
    public elRef: ElementRef,
    private _cdRef: ChangeDetectorRef
  ) {
    this.class = '';
    this.errorsShownOnBlur = ['email'];
    this.errorsShownOnSubmit = ['required'];
    this.errorsShownOnControlTouched = [
      'pattern',
      'minlength',
      'maxlength',
      'phone',
      'number',
      'lowercaseLetter',
      'uppercaseLetter',
      'latinLetters',
      'cardNumber',
    ];
  }

  ngOnInit(): void {
    this.submitErrors?.pipe(
      tap(() => {
        this._isFormSubmitted = true;
        this.clientValidationAnimation = !!this.errors;
      }),
      delay(1000)
    ).subscribe(() => {
      this.clientValidationAnimation = false;
      this._cdRef.markForCheck();
    });

    if (this.reset) {
      this.reset.subscribe(() => {
        this._isFormSubmitted = false;
      });
    }
  }

  public get fieldClass(): string {
    if (this.type === FieldType.checkbox) {
      return `checkboxCurrent form-field form-field--equal-margin ${this.class}`;
    } else if (this.type === FieldType.date) {
      return `date-row__item ${this.class}`;
    } else if (this.type === FieldType.custom) {
      return this.class;
    } else {
      return `form-field form-field--border-radius ${this.class}`;
    }
  }

  public get errorKeys(): string[] {
    return this._errorsToShow;
  }

  public get isErrorVisible(): boolean {
    if (this.errors) {
      const errors = Object.keys(this.errors);

      if (this._isFormSubmitted) {
        this._errorsToShow = errors;
        return true;
      }

      if (!this.isControlInFocus && errors.some(err => this.errorsShownOnBlur.includes(err))) {
        this._errorsToShow = errors.filter(_err => this.errorsShownOnBlur.includes(_err));
        return !!this._errorsToShow.length;
      }

      if (this.isControlTouched && errors.some(err => this.errorsShownOnControlTouched.includes(err))) {
        this._errorsToShow = errors.filter(_err => !this.errorsShownOnSubmit.includes(_err));
        return !!this._errorsToShow.length;
      }

      this._errorsToShow = errors
        .filter(_err => !this.errorsShownOnControlTouched.includes(_err)
          && !this.errorsShownOnSubmit.includes(_err)
          && !this.errorsShownOnBlur.includes(_err));
      return !!this._errorsToShow.length;
    }

    return false;
  }

}
